/**
 * Lightweight in-memory query cache with TTL.
 * Prevents re-fetching the same data on every page navigation.
 * Not a replacement for React Query — just a simple short-term cache.
 */

interface CacheEntry<T> {
  data: T
  timestamp: number
}

const cache = new Map<string, CacheEntry<unknown>>()
const DEFAULT_TTL_MS = 30_000 // 30 seconds

/**
 * TTL for data behind a tab that is unmounted when you leave it.
 *
 * Longer than the default on purpose, and safe to be: every consumer of this
 * constant paints the cached value and then revalidates in the background, so
 * the entry is never the last word on anything — it only decides whether the
 * reader waits for the answer or reads it while it is being rechecked.
 *
 * Thirty seconds was measured to be too short for the job. Reading one tab for
 * half a minute and switching back is ordinary use, and it landed on an expired
 * entry every time — the whole re-entry cost came straight back.
 */
export const TAB_DATA_TTL_MS = 5 * 60_000

export function getCached<T>(key: string, ttl = DEFAULT_TTL_MS): T | null {
  const entry = cache.get(key) as CacheEntry<T> | undefined
  if (!entry) return null
  if (Date.now() - entry.timestamp > ttl) {
    cache.delete(key)
    return null
  }
  return entry.data
}

export function setCached<T>(key: string, data: T): void {
  cache.set(key, { data, timestamp: Date.now() })
}

export function invalidateCache(keyPrefix: string): void {
  for (const key of cache.keys()) {
    if (key.startsWith(keyPrefix)) cache.delete(key)
  }
  for (const key of inflight.keys()) {
    if (key.startsWith(keyPrefix)) inflight.delete(key)
  }
}

/**
 * Requests currently in the air, keyed the same way as the cache.
 *
 * The cache alone cannot help two components that mount in the SAME tick — a
 * cache entry only exists once a response has come back, so both miss and both
 * ask. Measured on the «Deltakere» tab: `AttendeesTab` and `ShareLinksTab` each
 * mount `useEventRoster`, and `get_event_roster` went out twice, 0 ms apart,
 * for one screen. React's StrictMode double-invoke in dev is the same shape.
 *
 * The second caller now awaits the first caller's promise instead of opening
 * its own round-trip.
 */
const inflight = new Map<string, Promise<unknown>>()

/**
 * Run `fn` under `key`, collapsing concurrent callers onto one request.
 *
 * This is deliberately NOT a cache — it only covers the window while a request
 * is in flight. Pair it with `getCached`/`setCached` when the result should
 * also survive an unmount.
 */
export function dedupe<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const existing = inflight.get(key) as Promise<T> | undefined
  if (existing) return existing
  const promise = fn().finally(() => {
    // Only clear our own entry: an `invalidateCache` during the flight may
    // already have dropped it and a later caller may have started a new one.
    if (inflight.get(key) === promise) inflight.delete(key)
  })
  inflight.set(key, promise)
  return promise
}

export function clearCache(): void {
  cache.clear()
}
